import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Field, FormProvider, observer, useForm } from "@formily/react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/core/basic/Button";
import Loading from "../../../../components/core/blocks/Loading";
import { CurrencyInput } from "../../../../components/fields/formily/NumberInput";
import { useFormily } from "../../../../hooks/useFormily";
import type { PricingPlan } from "../../../../zodios/apiDefinitions/pricing";
import { useCharmPrice } from "../../api/useCharmPrice";
import { usePricingPlan } from "../../api/usePricingPlan";
import { usePricingPlanUpdate } from "../../api/usePricingPlanUpdate";
import { PlanSliderFormily } from "../PricingSlider";
import { DevErrorDisplay } from "../../../../components/core/blocks/DevErrorDisplay";
import { useFormSubmitErrorHandler } from "../../../../hooks/useFormSubmitErrorHandler";
import { Modal } from "../../../../components/core/basic/Modal";

interface Props {
  initialValues: PricingPlan;
}

export const QuickPlanConfigModal = NiceModal.create(({ planId }: { planId: number }) => {
  const { t } = useTranslation(["pricingv2"]);
  const modal = useModal();
  const planQuery = usePricingPlan({ id: planId }, { refetchOnMount: true });

  return (
    <Modal
      isOpen={modal.visible}
      title={planQuery.data?.plan_name ?? ""}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          modal.remove();
        }
      }}
      css={{ width: "60vw", padding: "xlarge" }}
    >
      {planQuery.isLoading || !planQuery.isFetchedAfterMount ?
        <Loading />
      : planQuery.isSuccess ?
        planQuery.data.plan_type === "competitor" ?
          t(($) => $.pricingv2["Competitor plans are not supported for quick config"])
        : <QuickPlanConfigForm initialValues={planQuery.data} />
      : <DevErrorDisplay
          userMessage={t(($) => $.pricingv2["There was an error loading the plan"])}
          error={planQuery.error}
        />
      }
    </Modal>
  );
});

function QuickPlanConfigForm({ initialValues }: Props) {
  const form = useFormily({ initialValues });
  return (
    <FormProvider form={form}>
      <QuickPlanConfigFormBody />
    </FormProvider>
  );
}

const QuickPlanConfigFormBody = observer(() => {
  const { t } = useTranslation(["pricingv2"]);
  const form = useForm<Props["initialValues"]>();

  const minCharmQuery = useCharmPrice({ price: form.values.min_price });
  const maxCharmQuery = useCharmPrice({ price: form.values.max_price });
  const updatePlanMutation = usePricingPlanUpdate();
  const handleSubmitError = useFormSubmitErrorHandler();
  const modal = useModal();
  return (
    <Col className={"px-5"}>
      <Row>
        <Col>
          <Field
            name="min_price"
            title={t(($) => $.pricingv2["Minimum Price"])}
            component={[CurrencyInput]}
            validator={{
              validator: (value) => {
                if (!value) {
                  return t(($) => $.pricingv2["Minimum price is required"]);
                }
                if (value > form.values.max_price) {
                  return t(($) => $.pricingv2["Minimum price cannot be greater than maximum price"]);
                }

                if (value < 0) {
                  return t(($) => $.pricingv2["Minimum price cannot be less than 0"]);
                }
                return true;
              },
            }}
          />
          {t(($) => $.pricingv2["The charmed minimum price will be {price}"], {
            price: minCharmQuery.data?.price ?? "...",
          })}
        </Col>
        <Col>
          <Field
            name="max_price"
            title={t(($) => $.pricingv2["Maximum Price"])}
            component={[CurrencyInput]}
            validator={{
              validator: (value) => {
                if (!value) {
                  return t(($) => $.pricingv2["Maximum price is required"]);
                }
                if (value < form.values.min_price) {
                  return t(($) => $.pricingv2["Maximum price cannot be less than minimum price"]);
                }

                if (value < 0) {
                  return t(($) => $.pricingv2["Maximum price cannot be less than 0"]);
                }
                return true;
              },
            }}
          />
          {t(($) => $.pricingv2["The charmed maximum price will be {price}"], {
            price: maxCharmQuery.data?.price ?? "...",
          })}
        </Col>
      </Row>
      {form.values.is_auto && (
        <Row className={"mt-3"} style={{ height: 75 }}>
          <Field name="configured_slider_value" component={[PlanSliderFormily]} />
        </Row>
      )}
      <Row className={"mt-5"}>
        <Button
          isDisabled={form.invalid || form.submitting}
          onPress={() => {
            form
              .submit(async (values) => {
                await updatePlanMutation.mutateAsync(
                  {
                    plan_id: values.id,
                    data: values,
                  },
                  { onSuccess: () => modal.remove() },
                );
              })
              .catch(handleSubmitError);
          }}
        >
          {t(($) => $.pricingv2["Submit"])}
        </Button>
      </Row>
    </Col>
  );
});
